import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

interface Props {
  kind: "image" | "video";
  max: number;
  value: string[];
  onChange: (urls: string[]) => void;
  folder?: string;
}

/** Uploads files to the public cms-images bucket and returns their public URLs. */
const MediaUploader = ({ kind, max, value, onChange, folder = "products" }: Props) => {
  const [busy, setBusy] = useState(false);
  const remaining = max - value.length;

  const handleFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    const list = Array.from(files).slice(0, remaining);
    if (files.length > remaining) toast.error(`Máximo de ${max} ${kind === "image" ? "fotos" : "vídeos"}`);
    setBusy(true);
    const urls: string[] = [];
    for (const f of list) {
      const ext = f.name.split(".").pop();
      const path = `${folder}/${kind}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
      const { error } = await supabase.storage.from("cms-images").upload(path, f, { contentType: f.type });
      if (error) { toast.error(`Erro ao enviar ${f.name}: ${error.message}`); continue; }
      urls.push(supabase.storage.from("cms-images").getPublicUrl(path).data.publicUrl);
    }
    onChange([...value, ...urls]);
    setBusy(false);
  };

  const move = (i: number) => {
    if (i === 0) return;
    const next = [...value];
    [next[0], next[i]] = [next[i], next[0]];
    onChange(next);
  };

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-3 gap-2">
        {value.map((url, i) => (
          <div key={url} className="relative rounded-lg overflow-hidden border border-border aspect-square bg-muted">
            {kind === "image" ? (
              <button type="button" onClick={() => move(i)} title="Definir como principal" className="w-full h-full">
                <img src={url} alt="" className="w-full h-full object-cover" />
              </button>
            ) : (
              <video src={url} className="w-full h-full object-cover" muted />
            )}
            {kind === "image" && i === 0 && (
              <span className="absolute bottom-1 left-1 text-[10px] bg-primary text-primary-foreground px-1.5 rounded">Principal</span>
            )}
            <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))}
              className="absolute top-1 right-1 bg-background/90 rounded-full p-1" aria-label="Remover">
              <X size={12} />
            </button>
          </div>
        ))}
      </div>
      {remaining > 0 && (
        <Button type="button" variant="outline" size="sm" className="gap-2" disabled={busy} asChild>
          <label className="cursor-pointer">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {busy ? "A enviar..." : `Carregar ${kind === "image" ? "fotos" : "vídeos"} (${value.length}/${max})`}
            <input type="file" hidden multiple accept={kind === "image" ? "image/*" : "video/*"}
              onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} />
          </label>
        </Button>
      )}
    </div>
  );
};

export default MediaUploader;
